import { Autocomplete, Box, FormControl, FormControlLabel, MenuItem, Radio, RadioGroup } from '@mui/material'
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateCertificateCache } from 'src/api/Certificates'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateDeviation } from 'src/api/deviation'
import { invalidateDocument } from 'src/api/document'
import { invalidateLocalityCache, useGetCustomerLocalities } from 'src/api/localities'
import {
  invalidateMainComponentCache,
  useChangeCageInMainComponent,
  useUpdateMainComponentToOutgoing,
} from 'src/api/main-component'
import { invalidateMaintenanceCache } from 'src/api/maintenance'
import { invalidateMooringCache } from 'src/api/moorings'
import { invalidatePositionCache, useGetCagePositionsByLocality } from 'src/api/positions'
import { invalidateScheduledMaintenanceCache } from 'src/api/scheduled-maintenance'
import { invalidateScheduledTaskCache } from 'src/api/scheduledTask'
import { useGetCustomerSuppliers } from 'src/api/supplier'
import { invalidateDocumentCategory } from 'src/api/users/document-category'
import { AppTextField } from 'src/components/AppTextField'
import BasicDatePicker from 'src/components/DateTimePicker'
import { AppForm, FormSubmitButton } from 'src/components/FormCore'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { APIResultCount, NO_VALID_CERTIFICATE } from 'src/constant'
import { IMainComponentType, MainComponentStatus, MovementType } from 'src/enums'
import { ILocality, IMooring, IPosition } from 'src/generated-types'
import { ChangeLocationType } from 'src/generated-types/change-location-type'
import { IMainComponent } from 'src/generated-types/main-component'
import { useZodForm } from 'src/hooks/zod-form'
import { MainComponentCertificationAdd } from 'src/screens/customer/mainComponents/MainComponentCertificationAdd'
import { certificateSchema } from 'src/screens/customer/mainComponents/mainComponentSchema'
import { ShowDialogProps } from 'src/show-dialog'
import { IFormSchemaReport } from 'src/generated-types/form-schema'
import { Flex } from 'src/styles/flexComponent'
import { sortByName, sortPositionsByReference, validateCertificateResponse } from 'src/utils'
import { getMooringName, getPositionName, getUTCDate } from 'src/utils/function'
import { z } from 'zod'
import { useGetFormSchemasForMovement } from 'src/api/form-schema'
import { useCompleteChecklistsDialog } from 'src/hooks/useCompleteChecklistsDialog'

interface ChangeLocationDialogProps extends ShowDialogProps {
  customerId: EntityId
  localityId: EntityId
  mainComponent: IMainComponent
  closeMainDialog?: () => void
  isChangeCageHidden?: boolean
}

const ChangeLocationDialog: FC<ChangeLocationDialogProps> = ({
  customerId,
  localityId,
  mainComponent,
  pop,
  closeMainDialog,
  isChangeCageHidden = false,
}) => {
  const { mutateAsync } = useChangeCageInMainComponent(customerId as EntityId, mainComponent.id as EntityId)
  const { mutate: sendToLocality } = useUpdateMainComponentToOutgoing(
    customerId as EntityId,
    mainComponent.id as EntityId,
  )
  const { data: localities } = useGetCustomerLocalities({ clientId: customerId as EntityId })
  const showChecklistsDialog = useCompleteChecklistsDialog(customerId as EntityId)
  const { data, isLoading: isLoadingPositions } = useGetCagePositionsByLocality(customerId, localityId)
  const { data: suppliers } = useGetCustomerSuppliers(customerId, 0, APIResultCount.Max)
  const { data: changePositionSchemas, isLoading: isLoadingChangePositionSchemas } = useGetFormSchemasForMovement(
    customerId,
    { equipmentTypeIds: [mainComponent.type.id as number], movementTypes: [MovementType.ChangePosition] },
  )
  const { data: sendToLocalitySchemas, isLoading: isLoadingSendToLocalitySchemas } = useGetFormSchemasForMovement(
    customerId,
    { equipmentTypeIds: [mainComponent.type.id as number], movementTypes: [MovementType.SendToLocality] },
  )

  const { t } = useTranslation(['common', 'main-component', 'certificate'])
  const [formType, setFormType] = useState<ChangeLocationType>(ChangeLocationType.MoveToLocality)
  const [selectedMooringId, setSelectedMooringId] = useState<EntityId | undefined>(mainComponent.position?.mooringId)

  const changeLocalitySchema = z
    .object({
      mainComponentId: z.number().default(mainComponent.id),
      selectedLocalityId: z.number(),
      formSchemaReports: z.array(z.any()).optional(),
    })
    .merge(
      !mainComponent.isValidCertificateAvailable &&
        mainComponent.type?.componentType === IMainComponentType.Net &&
        mainComponent.certificates?.length === 0
        ? certificateSchema
        : z.object({}),
    )

  const changePositionSchema = z
    .object({
      positionId: z.number(),
      positionName: z.string(),
      effectiveDate: z.date().or(z.string().transform(v => getUTCDate(new Date(v)) || new Date(v))),
      formSchemaReports: z.array(z.any()).optional(),
    })
    .merge(
      !mainComponent.isValidCertificateAvailable &&
        mainComponent.type?.componentType === IMainComponentType.Net &&
        mainComponent.certificates?.length === 0
        ? certificateSchema
        : z.object({}),
    )

  const form = useZodForm(formType == ChangeLocationType.MoveToPosition ? changePositionSchema : changeLocalitySchema, {
    defaultValues:
      formType == ChangeLocationType.MoveToPosition
        ? {
            positionId: mainComponent.positionId ?? 0,
            positionName: mainComponent.position?.name ?? '',
          }
        : {
            mainComponentId: mainComponent.id,
            selectedLocalityId: mainComponent.selectedLocalityId ?? 0,
          },
  })

  const [selectedPositionId, selectedLocalityId] = form.watch(['positionId', 'selectedLocalityId'])

  useEffect(() => {
    if (mainComponent?.status === MainComponentStatus.NewlyRegistered) {
      toast.info(t('sendToLocality', { ns: 'main-component' }))
      pop()
    }
  }, [mainComponent?.status, pop, t])

  const mooringList = useMemo(
    () =>
      (data ?? []).reduce((moorings, currentValue) => {
        if (!moorings.some(mooring => mooring.id == currentValue.mooringId)) {
          moorings.push({
            id: currentValue.mooringId,
            name: currentValue.mooringName,
          } as IMooring)
        }
        return moorings
      }, [] as IMooring[]) as IMooring[],
    [data],
  )

  const addCertificateDetailsToForm = useCallback(
    form => {
      const {
        certificateId,
        certificateNumber,
        certificateDate,
        validUntil,
        certificateDocumentType,
        certificateFileId,
        certificateSupplierId,
        validPeriod,
        ...rest
      } = form
      if (
        !mainComponent.isValidCertificateAvailable &&
        mainComponent.type?.componentType === IMainComponentType.Net &&
        mainComponent.certificates?.length === 0
      ) {
        rest.certificate = {
          certificateNumber,
          certificateDate,
          validUntil,
          validPeriod,
          certificateDocumentType,
          fileId: certificateFileId,
          supplierId: certificateSupplierId,
          mainComponentId: mainComponent.id,
        }
      }
      return rest
    },
    [mainComponent],
  )

  const performChangePosition = async (form: any, reports: IFormSchemaReport[]) => {
    await mutateAsync(
      addCertificateDetailsToForm({ ...form, mooringId: selectedMooringId, formSchemaReports: reports }),
      {
        onSuccess: () => {
          toast.success(t('cage-change-message', { ns: 'main-component' }))
          invalidateLocalityCache.getCageStatus(customerId, form.selectedLocalityId)
          invalidateMainComponentCache.invalidateEquipment(customerId)
          invalidateMainComponentCache.getMainComponentById(customerId, mainComponent.id as EntityId)
          invalidateMainComponentCache.getMainComponents(
            customerId as EntityId,
            form.selectedLocalityId as EntityId,
            form.selectedMooringId as EntityId,
            form.selectedPositionId,
          )
          invalidateMainComponentCache.getMainComponents(
            customerId as EntityId,
            mainComponent.selectedLocalityId as EntityId,
            mainComponent.position?.mooringId as EntityId,
            mainComponent.positionId,
          )
          invalidationActivityLogCache.getCageActivityLogs()
          invalidatePositionCache.getCagePositionDeviations(customerId, localityId, mainComponent.positionId)
          invalidateMainComponentCache.getMainComponentById(customerId as EntityId, mainComponent?.id as EntityId)
          invalidateMooringCache.getMooringsForMap(customerId, localityId)
          invalidateMooringCache.getGetMooring(customerId, localityId, selectedMooringId)
          invalidateScheduledMaintenanceCache.invalidateAllScheduledMaintenances(customerId, localityId as EntityId)
          invalidateMaintenanceCache.invalidateAllMaintenances(customerId, localityId as EntityId)
          invalidateCertificateCache.getCertificates(mainComponent?.id as EntityId)
          invalidateDeviation.getDeviations(customerId as EntityId)
          invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId)
          invalidateDocument.getDocuments(customerId)
          invalidateDocument.getDocumentsForApproval(customerId, localityId)
          invalidateLocalityCache.getCageStatus(customerId, localityId)
          invalidateLocalityCache.getLocality(customerId, localityId as EntityId)
          invalidateScheduledTaskCache.useGetEquipmentOfWorkOrder(form.selectedLocalityId as EntityId)
          const typeId = mainComponent?.typeId ?? mainComponent?.type?.id
          if (typeId != null) {
            invalidateMainComponentCache.getMainComponentsByComponentType(customerId, typeId as EntityId)
          }
          pop()
        },
        onError: error => {
          const errorData =
            (error as { response?: { data?: unknown }; data?: unknown })?.response?.data ??
            (error as { data?: unknown })?.data
          if (errorData === 'ExceedMaxCountForCage') {
            toast.error(t('exceedMaxCountForCage', { ns: 'main-component' }))
            return
          }
          if (typeof errorData === 'string') {
            validateCertificateResponse(errorData)
          } else {
            toast.error(t('updateFailed', { ns: 'common', defaultValue: 'Update failed' }))
          }
        },
      },
    )
  }

  const performChangeLocation = (form: any, reports: IFormSchemaReport[]) => {
    sendToLocality(addCertificateDetailsToForm({ ...form, formSchemaReports: reports }), {
      onSuccess: () => {
        toast.success(t('locality-change-message', { ns: 'main-component' }))
        invalidateLocalityCache.getCageStatus(customerId, form.selectedLocalityId)
        invalidateMainComponentCache.getMainComponents(
          customerId as EntityId,
          form.selectedLocalityId as EntityId,
          form.selectedMooringId as EntityId,
          form.selectedPositionId,
        )
        invalidateMainComponentCache.getMainComponents(
          customerId as EntityId,
          mainComponent.selectedLocalityId as EntityId,
          mainComponent.position?.mooringId as EntityId,
          mainComponent.positionId,
        )
        invalidateMainComponentCache.getOutgoingMainComponents(customerId, form.selectedLocalityId)
        invalidateCertificateCache.getCertificatesByPosition(customerId, form.selectedPositionId)
        invalidateMainComponentCache.getMainComponentsByComponentType(customerId, form.mainComponentTypeId as EntityId)
        invalidateMainComponentCache.invalidateEquipment(customerId)
        invalidateMainComponentCache.getMainComponentById(customerId as EntityId, mainComponent?.id as EntityId)
        invalidationActivityLogCache.getCageActivityLogs()
        invalidateScheduledMaintenanceCache.invalidateAllScheduledMaintenances(customerId, localityId)
        invalidateDeviation.getDeviations(customerId as EntityId)
        invalidateMooringCache.getMooringsForMap(customerId, localityId)
        invalidateDeviation.invalidateAllDeviations(customerId as EntityId, localityId)
        invalidateDeviation.getAllDeviationsForScheduledWorkOrders(customerId as EntityId, localityId as EntityId)
        invalidateCustomer.getCustomersActivityCounts(customerId)
        invalidateLocalityCache.getMaintenances(customerId, localityId)
        invalidateLocalityCache.getMaintenanceTypesCount(customerId, localityId)
        invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId)
        invalidateDocument.getDocuments(customerId)
        invalidateDocument.getDocumentsForApproval(customerId, localityId)
        invalidateLocalityCache.getCageStatus(customerId, localityId)
        invalidateLocalityCache.getLocality(customerId, localityId as EntityId)
        invalidateScheduledTaskCache.useGetEquipmentOfWorkOrder(form.selectedLocalityId as EntityId)
        invalidateLocalityCache.getLocalityWithActivityCounts(customerId, form.selectedLocalityId as EntityId)
        pop()
        if (closeMainDialog) closeMainDialog()
      },
      onError: error => {
        const errorData =
          (error as { response?: { data?: unknown }; data?: unknown })?.response?.data ??
          (error as { data?: unknown })?.data
        if (errorData === 'ExceedMaxCountForCage') {
          toast(t('exceedMaxCountForCage', { ns: 'main-component' }), { type: 'error' })
          return
        }
        if (errorData === NO_VALID_CERTIFICATE) {
          toast(t('NoValidCertificate', { ns: 'service-station' }), { type: 'error' })
          return
        }
        if (typeof errorData === 'string') {
          validateCertificateResponse(errorData)
        } else {
          toast.error(t('updateFailed', { ns: 'common', defaultValue: 'Update failed' }))
        }
      },
    })
  }

  const handleChangePosition = async (form: any) => {
    await showChecklistsDialog({
      schemas: changePositionSchemas,
      isLoadingSchemas: isLoadingChangePositionSchemas,
      confirmText: t('change-location', { ns: 'main-component' }),
      maxCountValidation: { positionId: form.positionId, equipmentIds: [mainComponent.id as EntityId] },
      onConfirm: reports => performChangePosition(form, reports),
    })
  }

  const handleChangeLocation = useCallback(
    async (form: any) => {
      await showChecklistsDialog({
        schemas: sendToLocalitySchemas,
        isLoadingSchemas: isLoadingSendToLocalitySchemas,
        confirmText: t('change-location', { ns: 'main-component' }),
        onConfirm: reports => performChangeLocation(form, reports),
      })
    },
    [mainComponent, sendToLocality, showChecklistsDialog, sendToLocalitySchemas],
  )

  const disabledAction = useMemo(() => {
    return formType == ChangeLocationType.MoveToPosition
      ? !selectedMooringId || selectedPositionId === mainComponent.positionId || !selectedPositionId
      : selectedLocalityId === mainComponent.selectedLocalityId || !selectedLocalityId
  }, [formType, mainComponent, selectedMooringId, selectedPositionId, selectedLocalityId])

  useEffect(() => {
    if (mainComponent.type?.componentType != IMainComponentType.Net) return
    if (!mainComponent.isValidCertificateAvailable && (mainComponent.certificates?.length ?? 0) > 0) {
      pop()
      if (mainComponent.isInvalidWithOneYearHold) {
        toast.error(t('invalidDueToYearHold', { ns: 'certificate' }))
        return
      }
      if ((mainComponent.extensionCount ?? 0) < 2 && mainComponent.status === MainComponentStatus.Active) {
        toast.error(t('addExtensionToRenewCertificate', { ns: 'certificate' }))
        return
      } else if (mainComponent.extensionCount === 2) {
        toast.error(t('noMoreExtensionsToAdd', { ns: 'certificate' }))
        return
      }
      toast.error(t('noValidCertificate', { ns: 'certificate' }))
    }
  }, [])

  return (
    <AppForm
      form={form}
      onSubmit={formType == ChangeLocationType.MoveToPosition ? handleChangePosition : handleChangeLocation}
    >
      <GenericDialogLayout
        title={t('change-location', { ns: 'main-component' })}
        pop={pop}
        removedCloseButton={false}
        actions={[
          <FormSubmitButton
            disabled={disabledAction}
            key="send"
            createText={t('change-location', { ns: 'main-component' })}
          />,
        ]}
        topActions={[
          <Flex.Row key={'top-action-list'} width={'100%'} px={4}>
            <FormControl key={'task-type-buttons-group'}>
              <RadioGroup
                value={formType}
                onChange={(_, v) => {
                  setFormType(v as unknown as ChangeLocationType)
                }}
                row
                name="task-type-buttons-group"
                sx={{ gap: 2 }}
              >
                <FormControlLabel
                  key={'move-to-locality'}
                  value={ChangeLocationType.MoveToLocality}
                  control={<Radio />}
                  label={`${t('move-to-locality', { ns: 'main-component' })}`}
                />
                {!isChangeCageHidden && (
                  <FormControlLabel
                    key={'move-to-position'}
                    value={ChangeLocationType.MoveToPosition}
                    control={<Radio />}
                    label={`${t('move-to-position', { ns: 'main-component' })}`}
                  />
                )}
              </RadioGroup>
            </FormControl>
          </Flex.Row>,
        ]}
      >
        {formType == ChangeLocationType.MoveToPosition && (
          <Fragment>
            <Autocomplete
              value={mooringList?.find(x => x.id === selectedMooringId) ?? null}
              onChange={(_, value) => {
                setSelectedMooringId((value as IMooring)?.id)
                form.setValue('positionId', 0)
                form.setValue('positionName', '')
              }}
              options={(mooringList?.sort(sortByName) || []) as IMooring[]}
              autoHighlight
              getOptionLabel={o => getMooringName(o.name)}
              renderInput={params => (
                <AppTextField
                  {...params}
                  label={t('selectMooring', { ns: 'main-component' })}
                  inputProps={{
                    ...params.inputProps,
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <Autocomplete
              value={(data ?? []).find(x => x.id === (selectedPositionId ?? mainComponent.positionId)) ?? null}
              onChange={(_, value) => {
                form.setValue('positionId', (value as IPosition)?.id ?? 0)
                form.setValue('positionName', (value as IPosition)?.name ?? '')
              }}
              disabled={!selectedMooringId}
              options={
                (data ?? [])
                  .filter(x => x.mooringId === selectedMooringId)
                  .sort(sortPositionsByReference) as IPosition[]
              }
              autoHighlight
              getOptionLabel={o => getPositionName(o) as string}
              renderOption={(props, o) => (
                <MenuItem {...props} key={o.id} value={o.id}>
                  {getPositionName(o) as string}
                </MenuItem>
              )}
              renderInput={params => (
                <AppTextField
                  {...params}
                  label={t('selectCage', { ns: 'main-component' })}
                  inputProps={{
                    ...params.inputProps,
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
            <Box py={1}>
              <BasicDatePicker label={t('effectiveDate', { ns: 'main-component' })} name="effectiveDate" />
            </Box>
          </Fragment>
        )}

        {formType == ChangeLocationType.MoveToLocality && (
          <Fragment>
            <Autocomplete
              value={
                (localities ?? []).find(l => l.id === (selectedLocalityId ?? mainComponent.selectedLocalityId)) ?? null
              }
              onChange={(_, value) => {
                const id = (value as ILocality)?.id
                if (id != null) {
                  form.setValue('selectedLocalityId', id)
                }
              }}
              options={(localities?.sort(sortByName) ?? []) as ILocality[]}
              autoHighlight
              getOptionLabel={o => o?.name ?? ''}
              renderOption={(props, o) => (
                <MenuItem {...props} key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              )}
              renderInput={params => (
                <AppTextField
                  {...params}
                  label={t('selectALocality', { ns: 'service-station' })}
                  inputProps={{
                    ...params.inputProps,
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Fragment>
        )}
        {mainComponent.type?.componentType === IMainComponentType.Net &&
          !mainComponent.isValidCertificateAvailable &&
          mainComponent.certificates?.length === 0 && (
            <MainComponentCertificationAdd
              suppliers={suppliers}
              accordingSummary={'product-certificate'}
              form={form}
              isProductCertificateAdd={false}
              mainComponentTypeDetails={{ mainComponentType: mainComponent.type, isCertificateDetailsRequired: true }}
            />
          )}
      </GenericDialogLayout>
    </AppForm>
  )
}

export default ChangeLocationDialog
